import React, { useContext } from 'react';
import { Button, makeStyles, shorthands, Text, tokens, typographyStyles } from '@fluentui/react-components';
import PropTypes from 'prop-types';
import { DeleteRegular } from '@fluentui/react-icons';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ResultsCount, SelectAllButton } from '@bingads-webui-fluent-component/entity-selector';
import { CampaignSelectorInline } from '@bingads-webui-campaign-react/campaign-adgroup-selector-v2';

const useLinkedInCampaignAssociationSelectorStyles = makeStyles({
  descriptionContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  description: {
    marginRight: '16px',
  },
  popoverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('8px', '16px'),
  },
  resultsCount: {
    ...typographyStyles.caption1Strong,
    color: tokens.colorNeutralForeground3,
  },
  selectAllButton: {
    color: tokens.colorBrandForegroundLink,
    fontWeight: tokens.fontWeightRegular,
  },
});

const LinkedInCampaignAssociationHeader = () => {
  const i18n = useContext(I18nContext);
  const classes = useLinkedInCampaignAssociationSelectorStyles();

  return (
    <div className={classes.popoverHeader}>
      <ResultsCount
        i18n={i18n}
        className={classes.resultsCount}
        label={i18n.getString(_TL_('LinkedIn Campaigns'))}
      />
      <SelectAllButton
        i18n={i18n}
        className={classes.selectAllButton}
        as="link"
      />
    </div>
  );
};

export const LinkedInCampaignAssociationSelector = ({
  cid, aid, associatedCampaigns, onAssociatedCampaignsChange,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useLinkedInCampaignAssociationSelectorStyles();

  return (
    <div className="linkedin-campaign-selector">
      <div className={classes.descriptionContainer}>
        <Text className={classes.description}>
          {i18n.getString(_TL_('Check the associated LinkedIn campaigns that you want to track the performance.'))}
        </Text>
        <Button
          className="remove-all-button"
          appearance="subtle"
          size="small"
          icon={<DeleteRegular />}
          onClick={() => onAssociatedCampaignsChange([])}
        >
          {i18n.getString(_TL_('Remove all'))}
        </Button>
      </div>
      <CampaignSelectorInline
        cid={cid}
        aid={aid}
        selectionMode="multiselect"
        options={{
          filter: '(CampaignType eq \'LinkedIn\')',
        }}
        searchboxProps={{
          'aria-label': i18n.getString(_TL_('Select LinkedIn campaigns to associate')),
          placeholder: i18n.getString(_TL_('Search for a LinkedIn campaign')),
        }}
        popOverContentBefore={(
          <LinkedInCampaignAssociationHeader />
        )}
        tagViewLimit={5}
        value={associatedCampaigns}
        onChange={onAssociatedCampaignsChange}
      />
    </div>
  );
};

LinkedInCampaignAssociationSelector.propTypes = {
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  associatedCampaigns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onAssociatedCampaignsChange: PropTypes.func.isRequired,
};
